const DEFAULT_VIEWPORT_BUFFER_PIXELS = 32;

function thresholdForType(thresholds, type) {
  const value = Number(thresholds?.[type]);
  return Number.isFinite(value) ? value : 0;
}

function isTypeVisible(byTypeVisible, type) {
  return byTypeVisible?.[type] !== false;
}

function bufferedExtent(extent, resolution, bufferPixels) {
  const buffer = Math.max(0, Number(resolution) || 0) * Math.max(0, bufferPixels);
  return [
    extent[0] - buffer,
    extent[1] - buffer,
    extent[2] + buffer,
    extent[3] + buffer
  ];
}

function coordinateInExtent(x, y, extent) {
  return x >= extent[0] && x <= extent[2] && y >= extent[1] && y <= extent[3];
}

function lowerBound(indices, coordinates, x) {
  let low = 0;
  let high = indices.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (Number(coordinates[indices[middle] * 2]) < x) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  return low;
}

function upperBound(indices, coordinates, x) {
  let low = 0;
  let high = indices.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (Number(coordinates[indices[middle] * 2]) <= x) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  return low;
}

export function createMalariaOverlaySpatialIndex(records) {
  const entriesByType = {};
  const count = Math.floor((records.coordinates?.length || 0) / 2);
  for (let index = 0; index < count; index++) {
    const type = records.types[index];
    (entriesByType[type] ||= []).push(index);
  }
  Object.values(entriesByType).forEach(indices => {
    indices.sort((left, right) => (
      Number(records.coordinates[left * 2]) - Number(records.coordinates[right * 2])
    ));
  });

  return {
    query(extent, requestedTypes = null) {
      const types = Array.isArray(requestedTypes) && requestedTypes.length
        ? requestedTypes
        : Object.keys(entriesByType);
      const indices = [];
      types.forEach(type => {
        const typeIndices = entriesByType[type] || [];
        const start = lowerBound(typeIndices, records.coordinates, extent[0]);
        const end = upperBound(typeIndices, records.coordinates, extent[2]);
        for (let position = start; position < end; position++) {
          const index = typeIndices[position];
          const y = Number(records.coordinates[index * 2 + 1]);
          if (y >= extent[1] && y <= extent[3]) {
            indices.push(index);
          }
        }
      });
      return indices.sort((left, right) => left - right);
    }
  };
}

function matchingRecordIndices(records, options, spatialIndex = null) {
  const extent = bufferedExtent(
    options.extent,
    options.resolution,
    options.viewportBufferPixels ?? DEFAULT_VIEWPORT_BUFFER_PIXELS
  );
  const indices = [];
  const count = Math.floor((records.coordinates?.length || 0) / 2);
  const requestedTypes = Array.isArray(options.types) && options.types.length
    ? new Set(options.types)
    : null;
  const candidateIndices = spatialIndex?.query
    ? spatialIndex.query(extent, requestedTypes ? Array.from(requestedTypes) : null)
    : Array.from({ length: count }, (_, index) => index);

  for (const index of candidateIndices) {
    const type = records.types[index];
    const confidence = Number(records.confidences[index]) || 0;
    const x = Number(records.coordinates[index * 2]);
    const y = Number(records.coordinates[index * 2 + 1]);
    if (
      (requestedTypes && !requestedTypes.has(type)) ||
      !isTypeVisible(options.byTypeVisible, type) ||
      confidence < thresholdForType(options.thresholds, type) ||
      !coordinateInExtent(x, y, extent)
    ) {
      continue;
    }
    indices.push(index);
  }
  return indices;
}

export function queryMalariaOverlay(records, options, spatialIndex = null) {
  return {
    indices: matchingRecordIndices(records, options, spatialIndex)
  };
}
